import { useTranslation } from '@/app/components/translations/Translation.tsx';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchPageWrapperData } from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { FilterGroupsComponents } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import {
  FilterChipsSection,
  SelectedFiltersSection,
  useModalChipSections,
} from '@/app/common/filters/surfaces/modal/FilterChipsSection.tsx';
import { hasActiveBadges } from '@/app/common/filters/surfaces/badges/FilterGroupsBadges.tsx';
import type { Filter, FiltersGroup, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import ModalFooter from '@/app/components/search/components/Modal/LayoutElements/ModalFooter';
import { MODAL_SIZES, type DialogCloseReason } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { applyUpdatedFilters, createUpdatedDynamicFiltersByOptions } from '@/app/common/filters/core/filterUtils.ts';
import { getSelectedFiltersCount } from '@/app/common/filters/core/filterSelectedUtils.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import {
  updateDynamicFilters,
  type DynamicFiltersUpdateResult,
} from '@/app/common/filters/searchParams/updateDynamicFilters.ts';
import { commitDraftFilters, filtersEqual, normaliseDraftFilters } from '@/app/common/filters/core/filterDraftUtils.ts';
import { newlySelectedFrom } from '@/app/common/filters/session/selections.ts';
import { usePressedOrder } from '@/app/common/filters/session/usePressedOrder.ts';
import type { CustomPillElementProps } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import LoadLine from '@/app/components/Loadline.tsx';
import Pill from '@/app/common/components/Pill';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useIsModalViewport } from '@/app/components/search/utils/useSsrMedia.ts';
import { createModalFilterGroups } from '@/app/common/filters/surfaces/modal/createModalFilterGroups.ts';
import { CUSTOM_GROUP_NAME } from '@/app/common/filters/core/filterNames.ts';
import {
  FILTER_SURFACE,
  getCustomFilterMainCreators,
} from '@/app/common/filters/surfaces/forms/custom/customFilterMainCreators.ts';
import { RESULT_PAGE_FILTER_RESET_POSITION } from '@/app/common/starTrack/events/resultPageFilterReset.ts';
import type {
  ResultPageFilterHighlighted,
  ResultPageFilterTracking,
} from '@/app/common/starTrack/events/resultPageFilter.ts';
import { urlParamOf } from '@/app/common/filters/core/filterDefinitions.ts';
import { RESULT_PAGE_FILTER_TRIGGER_SECTION } from '@/app/common/starTrack/events/resultPageFilter.ts';
import { FILTER_BADGE_LABEL_CREATORS } from '@/app/common/filters/surfaces/labels/filtersLabelsUtils.tsx';
import { filterGroupTitleId } from '@/app/common/filters/surfaces/forms/groupHeading.ts';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';
import { applyCtaLabel } from '@/app/common/filters/surfaces/labels/applyCtaLabel.ts';
import { StatusMessage } from '@/app/common/components/StatusMessage';
import { resultsFoundText } from '@/app/components/search/common/resultsFoundText.ts';

const MODAL_ID = 'all-filters-modal';
const OPEN_DROPDOWN = '[role="combobox"][aria-expanded="true"]';

const isInOpenDropdown = (event: object) => {
  const { target } = event as Event;
  return target instanceof Element && target.closest(OPEN_DROPDOWN) !== null;
};

export const AllFiltersPill = ({ isLoading, busy, disabled: greyedOut, filterPill }: CustomPillElementProps) => {
  const { commonData, searchInfoData, localization, searchParams } = usePageWrapperData();
  const { filtersGroups, handleFilterChanged, handleFiltersReset, filterSession } = useSearchPageWrapperData();
  const { productsData } = useSearchPageData();
  const { isMobile } = useCommonClient();
  const isModalViewport = useIsModalViewport(isMobile);
  const { t, translationDictionary } = useTranslation();
  const { useEvoFilters, totalResults } = useFiltersUiData();
  const allFiltersFiltersGroups = useMemo(() => createModalFilterGroups({ filtersGroups }), [filtersGroups]);
  const label = t('vacation.oss.filters.allfilters');
  const [allFiltersGroups, setAllFiltersGroups] = useState<FiltersGroups>(allFiltersFiltersGroups);
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [draftTotal, setDraftTotal] = useState<number | undefined>(undefined);
  const draftRequestSeq = useRef(0);
  const reopenGuard = useReopenGuard();
  const pressed = usePressedOrder();
  // Chips pressed in Recently used / Popular, reported as filterType when the draft is applied.
  const highlighted = useRef<ResultPageFilterHighlighted>({});
  // Not isUpdating: disabling the control just pressed would drop its keyboard focus.
  const controlsDisabled = isLoading || !!busy;
  const disabled = isUpdating || controlsDisabled;
  const filtersCount = getSelectedFiltersCount(allFiltersFiltersGroups);
  const hasSelectedFilters = filtersCount > 0;
  const isClearDisabled = disabled || !hasSelectedFilters;

  const flattenFilters = (groups: FiltersGroups): Filter[] =>
    groups.map((group) => (group as FiltersGroup).filters).flat();
  const draftFlat = useMemo(() => flattenFilters(allFiltersGroups), [allFiltersGroups]);
  const appliedFlat = useMemo(() => flattenFilters(allFiltersFiltersGroups), [allFiltersFiltersGroups]);

  const draftDiffers = useMemo(
    () => !filtersEqual(normaliseDraftFilters(draftFlat), appliedFlat),
    [draftFlat, appliedFlat]
  );
  // The applied total only describes the draft while the two agree; otherwise it is the wrong number.
  const shownTotal = draftDiffers ? draftTotal : totalResults;
  const applyText = useEvoFilters && shownTotal !== undefined ? applyCtaLabel(t, shownTotal) : undefined;
  // The CTA is the only gate on a zero-result combination; Reset all applies straight away.
  const isApplyDisabled = disabled || (useEvoFilters && shownTotal === 0);
  const isApplyLoading = !!useEvoFilters && isUpdating && draftDiffers;
  const statusMessage = isUpdating
    ? t('vacation.oss.components.searchfilters.main.loading')
    : draftTotal !== undefined
      ? resultsFoundText(t, draftTotal)
      : '';
  const showsSelectedRow = !!useEvoFilters && hasActiveBadges(allFiltersGroups);

  // The Selected row comes and goes above the control in focus, so keep that control in view.
  useLayoutEffect(() => {
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && document.getElementById(MODAL_ID)?.contains(focused))
      focused.scrollIntoView({ block: 'nearest' });
  }, [showsSelectedRow]);

  useEffect(() => {
    // Drops any response still in flight, on close as well as on open.
    draftRequestSeq.current += 1;

    if (isOpen) {
      pressed.reset();
      highlighted.current = {};
      setAllFiltersGroups(createModalFilterGroups({ filtersGroups }));
      setDraftTotal(undefined);
      setIsUpdating(false);
    }
    // Deliberately not keyed on filtersGroups: resyncing mid-session would throw away the draft.
  }, [isOpen]);

  const createDraftUpdateHandler =
    (seq: number) =>
    ({ updatedGroups, total }: DynamicFiltersUpdateResult) => {
      // Responses can still land out of order, so only the newest request may write.
      if (seq !== draftRequestSeq.current) return;

      if (updatedGroups) {
        setAllFiltersGroups((prevFiltersGroups) => {
          const newFiltersGroups = createModalFilterGroups({ filtersGroups: updatedGroups });
          return newFiltersGroups.map((newGroup, index) => {
            const prevGroup = prevFiltersGroups[index] as FiltersGroup;
            const newGroupTyped = newGroup as FiltersGroup;
            return {
              ...newGroupTyped,
              filters: createUpdatedDynamicFiltersByOptions({
                prevFilters: prevGroup?.filters ?? [],
                allUpdatedFilters: newGroupTyped.filters,
              }),
            };
          });
        });
      }

      setDraftTotal(total);
      setIsUpdating(false);
    };

  // A standard-UI edit of a param supersedes an earlier chip press on it: the value being applied no
  // longer comes from the chip, so its mark is dropped instead of leaking into the event.
  const rememberHighlighted = (updatedFilters: Filter[], tracking?: ResultPageFilterTracking) => {
    updatedFilters.forEach((filter) => {
      const urlParam = urlParamOf(filter.name);
      if (!urlParam) return;
      const filterType = tracking?.highlighted?.[urlParam];
      if (filterType) highlighted.current[urlParam] = filterType;
      else delete highlighted.current[urlParam];
    });
  };

  const handlePillFilterChanged: HandleFilterChanged = (updatedFilters, _pressedOrder, tracking) => {
    rememberHighlighted(updatedFilters, tracking);
    setIsUpdating(true);
    draftRequestSeq.current += 1;
    const seq = draftRequestSeq.current;
    const handleFiltersGroupsUpdated = createDraftUpdateHandler(seq);

    setAllFiltersGroups((prevFiltersGroups) => {
      const updatedFiltersGroups = applyUpdatedFilters({ prevFiltersGroups, updatedFilters });
      pressed.record(newlySelectedFrom(prevFiltersGroups, updatedFiltersGroups));
      updateDynamicFilters({
        filtersGroups: updatedFiltersGroups,
        updatedFilters,
        searchInfoData,
        productsData,
        commonData,
        localization,
        translationDictionary,
        rebuildGroups: true,
        isCancelled: () => seq !== draftRequestSeq.current,
        handleFiltersGroupsUpdated,
      });
      return updatedFiltersGroups;
    });
  };

  const { recentlyUsed, popular } = useModalChipSections({
    draftFiltersGroups: allFiltersGroups,
    productsData,
    searchParams,
    handleFilterChanged: handlePillFilterChanged,
    recentlyUsedChips: filterSession.recentlyUsed,
    config: localization?.configValuesApp,
  });

  const handleOnClick = () => {
    if (reopenGuard.blocked()) return;
    setIsOpen(!isOpen);
  };

  const handleOnClose = () => {
    reopenGuard.markClosed();
    setIsOpen(false);
  };

  const handleModalClose = (event: object, reason: DialogCloseReason) => {
    if (reason === 'escapeKeyDown' && isInOpenDropdown(event)) return;
    handleOnClose();
  };

  const handleResetAll = () => {
    setIsOpen(false);
    handleFiltersReset(RESULT_PAGE_FILTER_RESET_POSITION.POPUP);
  };

  const handleApply = () => {
    setIsOpen(false);

    if (!useEvoFilters) {
      handleFilterChanged(draftFlat);
      return;
    }

    commitDraftFilters(draftFlat, appliedFlat, handleFilterChanged, pressed.order(), {
      triggerSection: RESULT_PAGE_FILTER_TRIGGER_SECTION.POP_UP,
      highlighted: highlighted.current,
    });
  };

  const customFilterMainCreators = getCustomFilterMainCreators(useEvoFilters, FILTER_SURFACE.ALL_FILTERS);
  const chipRows: { id: CUSTOM_GROUP_NAME; label: string; render: () => React.ReactElement }[] = useEvoFilters
    ? [
        {
          id: CUSTOM_GROUP_NAME.SELECTED_FILTERS,
          label: t('vacation.oss.filters.selected.title'),
          render: () => (
            <SelectedFiltersSection
              labelledBy={filterGroupTitleId(CUSTOM_GROUP_NAME.SELECTED_FILTERS)}
              filtersGroups={allFiltersGroups}
              customFilterLabelCreators={FILTER_BADGE_LABEL_CREATORS}
              isLoading={controlsDisabled}
              handleFilterChanged={handlePillFilterChanged}
            />
          ),
        },
        {
          id: CUSTOM_GROUP_NAME.RECENTLY_USED_FILTERS,
          label: t('vacation.oss.filters.recent.title'),
          render: () => (
            <FilterChipsSection
              chips={recentlyUsed}
              disabled={controlsDisabled}
              labelledBy={filterGroupTitleId(CUSTOM_GROUP_NAME.RECENTLY_USED_FILTERS)}
            />
          ),
        },
        {
          id: CUSTOM_GROUP_NAME.POPULAR_FILTERS,
          label: t('vacation.oss.filters.popular.title'),
          render: () => (
            <FilterChipsSection
              chips={popular}
              disabled={controlsDisabled}
              labelledBy={filterGroupTitleId(CUSTOM_GROUP_NAME.POPULAR_FILTERS)}
            />
          ),
        },
      ].filter(({ id }) =>
        id === CUSTOM_GROUP_NAME.SELECTED_FILTERS
          ? showsSelectedRow
          : (id === CUSTOM_GROUP_NAME.RECENTLY_USED_FILTERS ? recentlyUsed : popular).length > 0
      )
    : [];

  const customGroupCreators = Object.fromEntries(chipRows.map(({ id, render }) => [id, render]));
  const modalFilterGroups = createModalFilterGroups({
    filtersGroups: allFiltersGroups,
    chipGroups: chipRows.map((row) => ({ id: row.id, label: row.label, filters: [] })),
  });

  const filtersGroupsComponents = (
    <FilterGroupsComponents
      filtersGroups={modalFilterGroups}
      customGroupCreators={customGroupCreators}
      customFilterMainCreators={customFilterMainCreators}
      isLoading={controlsDisabled}
      handleFilterChanged={handlePillFilterChanged}
    />
  );
  return (
    <>
      <Pill
        isLoading={isLoading}
        busy={busy}
        disabled={greyedOut}
        label={label}
        icon={filterPill?.icon}
        iconOnly={filterPill?.iconOnly}
        handleClick={handleOnClick}
        hasPopover={true}
        showChevron={!filterPill?.hideChevron && (useEvoFilters || !hasSelectedFilters)}
        selected={hasSelectedFilters}
        isOpen={isOpen}
        filtersCount={filtersCount}
        countBadge={useEvoFilters && !filterPill?.textCount}
        className={`pill pill-all-filters${hasSelectedFilters ? ' selected' : ''}`}
        controls={isOpen ? MODAL_ID : undefined}
      />
      <OpenXModal
        className="openx-ui-all-filters-modal"
        id={MODAL_ID}
        isOpen={isOpen}
        onClose={handleModalClose}
        isMobile={isModalViewport}
        brand={Brand.LASTMINUTE}
        scrollShadows={true}
        size={useEvoFilters ? MODAL_SIZES.L_EVO : MODAL_SIZES.XXL}
        bodyPadding={{ top: 0, hor: 3 }}
        fullScreen={false}
        aria-label={t('vacation.oss.filters.modal_title')}
        header={{
          sticky: true,
          element: (
            <ModalHeader handleClose={handleOnClose} evo={true} adornment={isUpdating && <LoadLine />}>
              {t('vacation.oss.filters.modal_title')}
            </ModalHeader>
          ),
        }}
        footer={{
          sticky: true,
          element: (
            <ModalFooter
              evo={true}
              sticky={true}
              applyText={applyText}
              handleApply={handleApply}
              isApplyDisabled={isApplyDisabled}
              isApplyLoading={isApplyLoading}
              handleClear={handleResetAll}
              isClearDisabled={isClearDisabled}
              clearText={t('vacation.oss.filters.reset_all')}
              layout={isModalViewport ? 'full-width' : 'right'}
            />
          ),
        }}
      >
        <>
          <StatusMessage message={statusMessage} />
          {filtersGroupsComponents}
        </>
      </OpenXModal>
    </>
  );
};
